import {PrismicRichText} from '@prismicio/react'
import type {ProductOverviewSliceDefault} from 'prismicio-types'
import type {FC} from 'react'
import type {Context} from 'src/common/types'
import {ProductOverviewCard} from './components/ProductCard'
import {CenterSeparators, Gradient, SideSeparators} from './components/separators'

/**
 * Props for `ProductOverview`.
 */
export type ProductOverviewDefaultVariationProps = {
  context: Context
  slice: ProductOverviewSliceDefault & {
    slice_type: string
    variation: string
  }
  componentIndex: number
}

/**
 * Component for "ProductOverview" Slices.
 */
export const ProductOverviewDefaultVariation: FC<ProductOverviewDefaultVariationProps> = ({
  slice,
  context,
  componentIndex,
}) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="flex flex-col gap-0 bg-moss-white pb-0 pt-10 md:gap-12 md:pt-20"
  >
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-3 px-5 md:gap-6 md:px-10 lg:px-5 xl:px-0">
      <PrismicRichText
        field={slice.primary.tag}
        components={{
          paragraph: ({children}) => (
            <p className="text-tg-body-s md:text-button-s font-semibold uppercase text-moss-grey-400">{children}</p>
          ),
          strong: ({children}) => <strong className="pr-3 font-semibold text-moss-blue">{children}</strong>,
        }}
      />
      <div>
        <PrismicRichText
          field={slice.primary.heading}
          components={{
            heading1: ({children}) => (
              <h2 className="md:text-tg-h1 text-[32px] font-normal leading-[30px] tracking-[-.32px] text-moss-grey-400">
                {children}
              </h2>
            ),
            strong: ({children}) => (
              <>
                <span className="text-black">{children}</span>
                <br className="max-lg:hidden" />
              </>
            ),
          }}
        />
      </div>
    </div>
    <div
      className="
      relative
      isolate
      grid
      grid-cols-[20px_1fr_20px]
      grid-rows-[40px_auto_20px_auto_20px_auto_20px_auto_20px_auto_20px_auto_40px]
      md:grid-cols-[20px_20px_1fr_24px_1fr_20px_20px]
      md:grid-rows-[64px_minmax(324px,_auto)_48px_minmax(324px,_auto)_48px_minmax(324px,_auto)_80px]
      lg:grid-cols-[20px_minmax(0,385px)_24px_minmax(0,385px)_24px_minmax(0,385px)_20px]
      lg:grid-rows-[64px_1fr_48px_1fr_80px]
      xl:grid-cols-[1fr_60px_minmax(0,385px)_24px_minmax(0,385px)_24px_minmax(0,385px)_60px_1fr]
      "
    >
      <Gradient color="255, 255, 255" gradientStart={0} height={24} location="top" />
      {slice.primary.product_sections.map((content, index) => (
        <ProductOverviewCard
          content={content}
          index={index}
          key={index}
          componentIndex={componentIndex}
          pageUid={context.pageUid}
        />
      ))}

      <CenterSeparators />
      <SideSeparators />
      <Gradient color="255, 255, 255" gradientStart={20} height={32} location="bottom" />
    </div>
  </section>
)
